// @ts-ignore
/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useCallback, useEffect, useRef, useState } from 'react'
import req from '@sportninja/common/api/request'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'

const ProgressBar = ({ completed }) => {
  return (
    <div
      css={css`
        height: 35px;
        width: '100%';
        background-color: #e0e0de;
        border-radius: 5px;
      `}
    >
      <div
        css={css`
          height: 100%;
          width: ${completed}%;
          background-color: ${colors.DEFAULT_FLAIR};
          border-radius: inherit;
          text-align: right;
          transition: width 1s ease-in-out;
          justify-content: center;
          align-items: center;
          display: flex;
        `}
      >
        <span
          css={css`
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
            color: white;
            font-size: 15px;
            font-weight: 'bold';
          `}
        >
          {completed > 0 ? `${completed}%` : ''}
        </span>
      </div>
    </div>
  )
}

let gameIndex = 0
let gamesSuccessfullyProcessed = 0
let lastIndexWithError = -1

export default function ScoresheetsFirefox({ history, location }) {
  const [loading, setLoading] = useState(true)
  const [hasError, setErrorExists] = useState(false)
  const [readyToPrint, setReadyToPrint] = useState(false)
  const [totalGamesProcessed, setTotalGamesProcessed] = useState(-1)
  const [abortControllers, setAbortControllers] = useState<AbortController[]>(
    []
  )
  const [HTMLLength] = useState(
    (location && location.state && location.state.games.length) || 0
  )

  let games = (location && location.state && location.state.games) || []
  const isSoccer = location.state?.isSoccer || false

  const iframeContainer = useRef<HTMLIFrameElement>(null)

  /**
   * Set up landscape print orientation
   * This ensures scoresheets always print in landscape mode
   */
  useEffect(() => {
    const printStyles = document.createElement('style')
    printStyles.textContent = `
      @media print {
        @page {
          size: landscape;
        }
        body {
          margin: 0;
          padding: 0;
        }
      }
    `

    if (!isSoccer) {
      document.head.appendChild(printStyles)
    }

    return () => {
      // Clean up the style element when component unmounts
      if (document.head.contains(printStyles)) {
        document.head.removeChild(printStyles)
      }
    }
  }, [isSoccer])

  // Replace your loadGames function with this version
  const loadGames = useCallback(async () => {
    try {
      const printContents = []

      for (const i in games) {
        if (Number(i) > lastIndexWithError) {
          setTotalGamesProcessed((prev) => prev + 1)
          const abortController = new AbortController()
          const signal = abortController.signal
          setAbortControllers((state) => {
            state.push(abortController)
            return state
          })

          gameIndex = Number(i)

          try {
            const response = await req(
              `/games/${games[Number(i)]}/scoresheets/html`,
              {
                parseJSON: false,
                signal,
              }
            )

            const html = await response.text()
            // @ts-ignore
            printContents.push(html)
            gamesSuccessfullyProcessed += 1
          } catch (error) {
            console.error(`Error loading game ${games[Number(i)]}:`, error)
            // Continue with other games
          }
        }
      }

      // Create a single print document with all scoresheets
      if (printContents.length > 0) {
        const printFrame = document.createElement('iframe')
        printFrame.style.position = 'absolute'
        printFrame.style.width = '100%'
        printFrame.style.height = '100%'
        printFrame.style.border = 'none'
        printFrame.style.top = '0'
        printFrame.style.left = '0'
        printFrame.id = 'print-frame'

        // Add to container
        iframeContainer.current?.appendChild(printFrame)

        // Remove this from the .scoresheet-page class, to fix the issue with the soccer scoresheets
        // ${
        //   isSoccer ? 'transform: scale(0.60); transform-origin: 0 0;' : ''
        // }
        // Create a document with all scoresheets, each in its own div with page breaks
        const combinedHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>ScoresheetsFirefox</title>
          <style>
            body { margin: 0; padding: 0; }
            .scoresheet-page {
              page-break-after: always;
              page-break-inside: avoid;
        
            }
            @page { size: landscape; margin: 0; }
            @media print {
              body { margin: 0; padding: 0; }
            }
          </style>
        </head>
        <body>
          ${printContents
            .map((content) => `<div class="scoresheet-page">${content}</div>`)
            .join('')}
        </body>
        </html>
      `

        // Write to the iframe
        printFrame.onload = () => {
          setTotalGamesProcessed(games.length)
          setReadyToPrint(true)
        }

        const frameDoc = printFrame.contentWindow?.document
        frameDoc?.open()
        frameDoc?.write(combinedHTML)
        frameDoc?.close()
      } else {
        setErrorExists(true)
        setLoading(false)
      }
    } catch (error) {
      if (games.length <= 1) {
        setErrorExists(true)
        setLoading(false)
      } else {
        saveGameWithErrorAndProceedToNextGame()
      }
    }
  }, [isSoccer])

  const saveGameWithErrorAndProceedToNextGame = useCallback(() => {
    lastIndexWithError = gameIndex

    loadGames()
  }, [])

  useEffect(() => {
    if (readyToPrint && !hasError) {
      setLoading(false)
      setTimeout(() => {
        if (gamesSuccessfullyProcessed > 0) {
          // For Firefox, we need to use the iframe's print function
          const isFirefox = navigator.userAgent.indexOf('Firefox') !== -1

          if (isFirefox) {
            const printFrame = document.getElementById('print-frame')
            // @ts-ignore
            if (printFrame && printFrame.contentWindow) {
              // @ts-ignore
              printFrame.contentWindow.print()
            } else {
              window.print()
            }
          } else {
            window.print()
          }
        }
      }, 500) // Increased delay to ensure everything is rendered
    }
  }, [readyToPrint, hasError])

  useEffect(() => {
    loadGames()

    return () => {
      gameIndex = 0
      gamesSuccessfullyProcessed = 0
      lastIndexWithError = -1

      abortControllers.forEach((controller) => {
        controller.abort()
      })
    }
  }, [])

  return (
    <>
      <div
        css={css`
          padding: 50px;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow: hidden;

          @media only print {
            visibility: hidden;
            display: none; // Add this line
          }
        `}
      >
        {loading ? (
          <>
            <h1
              css={css`
                font-size: 40px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                margin-bottom: 2em;
                font-weight: bold;
              `}
            >
              Your scoresheets are being processed. Please wait.
            </h1>
            <div
              css={css`
                width: 60%;
              `}
            >
              <ProgressBar
                completed={Math.round(
                  (totalGamesProcessed / games.length) * 100
                )}
              />
            </div>
          </>
        ) : (
          <>
            <h1
              css={css`
                font-size: 40px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                margin-bottom: 2em;
                font-weight: bold;
                text-align: center;
              `}
            >
              {hasError || gamesSuccessfullyProcessed === 0
                ? 'There is a problem processing the scoresheet, please try again later.'
                : 'Your scoresheets are ready to download.'}
            </h1>
            <p
              css={css`
                font-size: 14px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                margin-bottom: 2.5em;
                font-weight: bold;
              `}
            >
              A total of {gamesSuccessfullyProcessed} / {HTMLLength} scoresheets
              were generated.
            </p>
            {gamesSuccessfullyProcessed > 0 && (
              <a
                css={css`
                  font-size: 15px;
                  font-weight: bold;
                  color: ${colors.DEFAULT_FLAIR};
                  margin-left: 8px;
                  margin-bottom: 2.5em;
                  font-family: Arial, Helvetica, sans-serif;
                `}
                onClick={() => {
                  const printFrame = document.getElementById('print-frame')
                  // @ts-ignore
                  if (printFrame && printFrame.contentWindow) {
                    // @ts-ignore
                    printFrame.contentWindow.print()
                  } else {
                    window.print()
                  }
                }}
              >
                {hasError
                  ? `Show the ${gamesSuccessfullyProcessed} games processed`
                  : 'Click here to open print dialog again.'}
              </a>
            )}
            <button
              type='button'
              onClick={() => {
                games = []
                gamesSuccessfullyProcessed = 0
                gameIndex = 0
                history.goBack()
              }}
              css={css`
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                font-size: 24px;
                font-weight: bold;
                color: white;
                margin-left: 8px;
              `}
            >
              {'<'} Back
            </button>
          </>
        )}
      </div>
      <div
        css={css`
          visibility: hidden;
          height: 0;
          overflow: hidden;

          @media print {
            visibility: visible;
            height: auto;
            overflow: visible;
          }
        `}
        ref={iframeContainer}
      />
    </>
  )
}
